import { useSession } from '@/lib/session';

/**
 * The durations offered in a duration field's dropdown.
 *
 * Steps of the RECORDING INCREMENT, up to ten hours, which is what the previous
 * system offered and for the reason it offered it: a preset that is not a multiple
 * of the step is a value the save refuses, so a hardcoded list would hand people
 * the very error this dropdown exists to save them from. Ten hours because these
 * are days worked, not stopwatch readings - longer than that is typed by hand.
 *
 * A native `<datalist>` rather than a select or a combobox, because a datalist
 * SUGGESTS rather than restricts: "1:20", "7.5" and "90m" still go in by hand, it
 * filters as you type, and the keyboard stays faster than the picker. The server
 * agrees - it stores what was typed, and the step is only ever a suggestion.
 *
 * Here rather than in the grid, because the grid, the day form and the bulk fill
 * all ask for a duration and all three should offer the same list.
 */
export const DURATION_LIST_ID = 'duration-presets';

const MAX_MINUTES = 10 * 60;

/** Render once anywhere on the page; every `list={DURATION_LIST_ID}` field uses it. */
export function DurationPresets() {
  const { data: session } = useSession();
  // 15 until the session lands, so the list is never empty on first paint. The
  // server is the one that enforces the step; this only decides what is offered.
  const step = session?.durationIncrementMinutes ?? 15;

  const presets: string[] = [];
  for (let minutes = step; minutes <= MAX_MINUTES; minutes += step) {
    presets.push(`${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`);
  }

  return (
    <datalist id={DURATION_LIST_ID}>
      {presets.map((d) => (
        <option key={d} value={d} />
      ))}
    </datalist>
  );
}
